var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import { OutcomeChanges } from "./OutcomeChangeNotice";
import { useId, useRef, useState } from "react";
import { useNotify } from "react-admin";
import { Alert, Box, Button, CircularProgress, Drawer, Link, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography, } from "@mui/material";
import { useApolloClient, useMutation } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { GET_UPLOAD_URL } from "@/queries/GetUploadUrl";
import { IMPORT_SCHEDULE, PREVIEW_SCHEDULE_IMPORT, } from "@/queries/Lifecycle";
import { IPermissions } from "@/types/keycloak";
import { getGraphQLActionErrorReason } from "@/services/graphqlActionError";
import { formatWallTime, useTimeZoneService } from "@/components/timezones/timeZoneService";
/** The CSV columns (design §6); the template has the header and two example rows. */
export var SCHEDULE_CSV_COLUMNS = [
    "election_alias",
    "event_type",
    "local_date_time",
    "timezone",
    "voting_channels",
];
var TEMPLATE_ROWS = [
    ["ALL", "END_VOTING_PERIOD", "2028-05-08T19:00", "", ""],
    ["<election alias>", "START_VOTING_PERIOD", "2028-04-09T00:00", "", "ONLINE|KIOSK"],
];
/** Rows shown before "…and N more rows". */
var SHOWN_ROWS = 7;
/** The error codes `preview_schedule_import` reports per row (stream C). */
var ERROR_KEYS = {
    "unknown-election": "unknownElection",
    "ambiguous-election": "ambiguousElection",
    "unknown-event-type": "unknownEventType",
    "invalid-time-zone": "invalidTimeZone",
    "invalid-date-time": "invalidDateTime",
    "invalid-voting-channels": "invalidVotingChannels",
    "dst-gap": "dstGap",
    "duplicate": "duplicate",
};
/** Errors first, then in file order. */
export var previewOrder = function (rows) {
    return __spreadArray([], rows, true).sort(function (a, b) { return Number(!a.error_code) - Number(!b.error_code) || a.row - b.row; });
};
var templateUrl = function () {
    var csv = __spreadArray([SCHEDULE_CSV_COLUMNS.join(",")], TEMPLATE_ROWS.map(function (row) { return row.join(","); }), true);
    return URL.createObjectURL(new Blob([csv.join("\n") + "\n"], { type: "text/csv" }));
};
/** One preview row: its time in its zone and in the primary, or what's wrong with it. */
var PreviewRow = function (_a) {
    var _b, _c, _d;
    var row = _a.row, primary = _a.primary;
    var t = useTranslation().t;
    var service = useTimeZoneService();
    // Spreadsheets may write a space for the T.
    var local = row.local.trim().replace(" ", "T").slice(0, 16);
    var error = row.error_code;
    // A time in a DST gap doesn't exist: the import refuses it (design §1 defaults).
    var errorText = error
        ? t("lifecycle.import.error.".concat((_b = ERROR_KEYS[error]) !== null && _b !== void 0 ? _b : "other"), {
            code: error,
            zone: row.time_zone,
            election: row.election_alias,
            type: row.event_type,
            dateTime: formatWallTime(local, service.text),
            city: service.zoneCity(row.time_zone, service.text),
        })
        : null;
    return (_jsxs(TableRow, { "data-testid": "import-row-".concat(row.row), sx: error ? { bgcolor: "rgba(237, 108, 2, 0.08)" } : undefined, children: [_jsx(TableCell, { children: row.row }), _jsx(TableCell, { children: (_c = row.election_name) !== null && _c !== void 0 ? _c : row.election_alias }), _jsx(TableCell, { children: t("eventsScreen.eventType.".concat(row.event_type), { defaultValue: row.event_type }) }), _jsxs(TableCell, { children: [row.instant && !error ? (_jsxs(_Fragment, { children: [_jsx(Typography, { variant: "body2", children: service.formatDateTimeZone(row.instant, row.time_zone, service.text) }), row.time_zone !== primary ? (_jsx(Typography, { variant: "body2", color: "text.secondary", children: service.formatDateTimeZone(row.instant, primary, service.text) })) : null] })) : (_jsx(Typography, { variant: "body2", children: t("lifecycle.import.asWritten", {
                            local: row.local,
                            place: (_d = row.election_name) !== null && _d !== void 0 ? _d : row.election_alias,
                        }) })), errorText ? (
                    // The theme's warning colours are too faint on the row's tint (axe color-contrast).
                    _jsx(Typography, { variant: "body2", sx: { color: "#8a3a00" }, children: errorText })) : row.note_code === "dst-overlap" && !error ? (_jsx(Typography, { variant: "body2", color: "text.secondary", children: service.zonedTimeNote(local, row.time_zone, service.text) })) : null] })] }));
};
/**
 * Import schedule: upload a CSV (one row per event and election, in local
 * time), check every row in its zone and in the primary, and import only a
 * file without errors. Importing again updates the same events.
 */
export var ImportScheduleDrawer = function (_a) {
    var _b, _c, _d;
    var electionEventId = _a.electionEventId, primary = _a.primary, onClose = _a.onClose, onImported = _a.onImported;
    var t = useTranslation().t;
    var notify = useNotify();
    var client = useApolloClient();
    var titleId = useId();
    var fileInput = useRef(null);
    var _e = useState(null), file = _e[0], setFile = _e[1];
    var _f = useState(null), preview = _f[0], setPreview = _f[1];
    var _g = useState(false), busy = _g[0], setBusy = _g[1];
    var getUploadUrl = useMutation(GET_UPLOAD_URL)[0];
    var _h = useMutation(IMPORT_SCHEDULE, { context: { headers: { "x-hasura-role": IPermissions.SCHEDULED_EVENT_WRITE } } }), importSchedule = _h[0], importing = _h[1].loading;
    var upload = function (chosen) { return __awaiter(void 0, void 0, void 0, function () {
        var data, target, response, checked, error_1;
        var _a;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    setBusy(true);
                    setPreview(null);
                    setFile(null);
                    _b.label = 1;
                case 1:
                    _b.trys.push([1, 5, 6, 7]);
                    return [4 /*yield*/, getUploadUrl({
                            variables: {
                                name: chosen.name,
                                media_type: chosen.type || "text/csv",
                                size: chosen.size,
                                is_public: false,
                                election_event_id: electionEventId,
                            },
                        })];
                case 2:
                    data = (_b.sent()).data;
                    target = data === null || data === void 0 ? void 0 : data.get_upload_url;
                    if (!(target === null || target === void 0 ? void 0 : target.url))
                        throw new Error("No upload URL");
                    return [4 /*yield*/, fetch(target.url, {
                            method: "PUT",
                            headers: { "Content-Type": chosen.type || "text/csv" },
                            body: chosen,
                        })];
                case 3:
                    response = _b.sent();
                    if (!response.ok)
                        throw new Error("Upload failed");
                    return [4 /*yield*/, client.mutate({
                            mutation: PREVIEW_SCHEDULE_IMPORT,
                            variables: { electionEventId: electionEventId, documentId: target.document_id },
                            context: { headers: { "x-hasura-role": IPermissions.SCHEDULED_EVENT_WRITE } },
                        })];
                case 4:
                    checked = (_b.sent()).data;
                    if (!(checked === null || checked === void 0 ? void 0 : checked.preview_schedule_import))
                        throw new Error("No preview");
                    setFile({ name: chosen.name, documentId: target.document_id });
                    setPreview(checked.preview_schedule_import);
                    return [3 /*break*/, 7];
                case 5:
                    error_1 = _b.sent();
                    notify((_a = getGraphQLActionErrorReason(error_1)) !== null && _a !== void 0 ? _a : t("lifecycle.import.uploadError"), {
                        type: "error",
                    });
                    return [3 /*break*/, 7];
                case 6:
                    setBusy(false);
                    return [7 /*endfinally*/];
                case 7: return [2 /*return*/];
            }
        });
    }); };
    var onImport = function () { return __awaiter(void 0, void 0, void 0, function () {
        var data, error_2;
        var _a, _b, _c, _d, _e;
        return __generator(this, function (_f) {
            switch (_f.label) {
                case 0:
                    if (!file)
                        return [2 /*return*/];
                    _f.label = 1;
                case 1:
                    _f.trys.push([1, 3, , 4]);
                    return [4 /*yield*/, importSchedule({
                            variables: { electionEventId: electionEventId, documentId: file.documentId },
                        })];
                case 2:
                    data = (_f.sent()).data;
                    notify(t("lifecycle.import.imported", {
                        created: (_b = (_a = data === null || data === void 0 ? void 0 : data.import_schedule) === null || _a === void 0 ? void 0 : _a.created) !== null && _b !== void 0 ? _b : 0,
                        updated: (_d = (_c = data === null || data === void 0 ? void 0 : data.import_schedule) === null || _c === void 0 ? void 0 : _c.updated) !== null && _d !== void 0 ? _d : 0,
                    }), { type: "success" });
                    onImported();
                    return [3 /*break*/, 4];
                case 3:
                    error_2 = _f.sent();
                    notify((_e = getGraphQLActionErrorReason(error_2)) !== null && _e !== void 0 ? _e : t("lifecycle.import.importError"), {
                        type: "error",
                    });
                    return [3 /*break*/, 4];
                case 4: return [2 /*return*/];
            }
        });
    }); };
    var rows = preview ? previewOrder(preview.rows) : [];
    var clean = !!preview && preview.errors === 0;
    return (_jsx(Drawer, { anchor: "right", open: true, onClose: onClose, slotProps: { paper: { "aria-labelledby": titleId } }, sx: { "& .MuiDrawer-paper": { width: { xs: "100%", md: 900 }, maxWidth: "100%" } }, children: _jsxs(Stack, { spacing: 2, sx: { width: "100%", boxSizing: "border-box", p: 3 }, children: [_jsxs(Box, { children: [_jsx(Typography, { id: titleId, variant: "h5", component: "h2", children: t("lifecycle.import.title") }), _jsx(Typography, { variant: "body2", color: "text.secondary", children: t("lifecycle.import.subtitle") })] }), _jsxs(Stack, { direction: "row", spacing: 2, sx: { alignItems: "center" }, children: [_jsx(Button, { variant: "contained", disabled: busy || importing, onClick: function () { var _a; return (_a = fileInput.current) === null || _a === void 0 ? void 0 : _a.click(); }, startIcon: busy ? _jsx(CircularProgress, { size: 14, "aria-hidden": true }) : undefined, children: (_b = file === null || file === void 0 ? void 0 : file.name) !== null && _b !== void 0 ? _b : t("lifecycle.import.chooseFile") }), _jsx("input", { ref: fileInput, type: "file", accept: ".csv,text/csv", hidden: true, "data-testid": "schedule-file", onChange: function (event) {
                                var _a;
                                var chosen = (_a = event.target.files) === null || _a === void 0 ? void 0 : _a[0];
                                event.target.value = "";
                                if (chosen)
                                    upload(chosen).catch(function () { return undefined; });
                            } }), _jsx(Link, { component: "button", type: "button", onClick: function () {
                                var url = templateUrl();
                                var anchor = document.createElement("a");
                                anchor.href = url;
                                anchor.download = t("lifecycle.import.templateFileName");
                                anchor.click();
                                URL.revokeObjectURL(url);
                            }, children: t("lifecycle.import.template") })] }), preview ? (_jsx(Alert, { severity: clean ? "success" : "warning", "data-testid": "import-summary", children: clean
                        ? t("lifecycle.import.ready", { ok: preview.ok, posts: preview.posts })
                        : t("lifecycle.import.needsAttention", {
                            ok: preview.ok,
                            posts: preview.posts,
                            count: preview.errors,
                        }) })) : null, preview ? (_jsxs(_Fragment, { children: [_jsx(OutcomeChanges, { changes: (_c = preview.outcome_changes) !== null && _c !== void 0 ? _c : [], zone: (_d = preview.primary_time_zone) !== null && _d !== void 0 ? _d : primary }), _jsxs(Table, { size: "small", "aria-label": t("lifecycle.import.preview"), children: [_jsx(TableHead, { children: _jsxs(TableRow, { children: [_jsx(TableCell, { children: t("lifecycle.import.row") }), _jsx(TableCell, { children: t("eventsScreen.fields.electionId") }), _jsx(TableCell, { children: t("eventsScreen.fields.eventProcessor") }), _jsx(TableCell, { children: t("eventsScreen.fields.scheduledDate") })] }) }), _jsx(TableBody, { children: rows.slice(0, SHOWN_ROWS).map(function (row) {
                                        var _a;
                                        return (_jsx(PreviewRow, { row: row, primary: (_a = preview.primary_time_zone) !== null && _a !== void 0 ? _a : primary }, row.row));
                                    }) })] }), rows.length > SHOWN_ROWS ? (_jsx(Typography, { variant: "body2", color: "text.secondary", children: t("lifecycle.import.moreRows", { count: rows.length - SHOWN_ROWS }) })) : null] })) : null, _jsxs(Stack, { direction: "row", spacing: 1, sx: { justifyContent: "flex-end" }, children: [_jsx(Button, { onClick: onClose, children: t("common.label.cancel") }), _jsx(Button, { variant: "contained", disabled: !clean || importing, onClick: onImport, children: t("common.label.import") })] })] }) }));
};
